import React, { useEffect, useRef } from "react";

import { CandidatesSection } from "@modules/ServiceRequest/Components/CandidatesSection";

import { Container } from "@UI/Components/Container/Container";
import { VStack, Stack } from "@UI/Components/Stack/Stack";
import {
  Wizard,
  WizardContent,
  WizardNav,
} from "@Shared/Components/Layouts/Wizard/Wizard";
import { BackLink } from "@Shared/Components/BackLink/BackLink";
import { ServiceRequestDetailCard } from "@modules/ServiceRequest/Components/ServiceRequestDetailCard/ServiceRequestDetailCard";
import { ServiceRequestDetailCardAiAssisted } from "@modules/ServiceRequest/Components/ServiceRequestDetailCard/ServiceRequestDetailCardAiAssisted";
import { ServiceRequestDetailCardSpInitiated } from "@modules/ServiceRequest/Components/ServiceRequestDetailCard/ServiceRequestDetailCardSpInitiated";
import ServiceRequestNextSteps from "@modules/ServiceRequest/Components/ServiceRequestNextSteps";
import { withFlow } from "@Libs/Flow/hocs/withFlow";
import { useFlow } from "@Libs/Flow/hooks/useFlow";
import { ServiceRequestFlow } from "@modules/ServiceRequest/Flows/ServiceRequestFlow";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useParams } from "@Libs/Router/hooks/useParams";
import { useServiceRequestFlow } from "@modules/ServiceRequest/hooks/useServiceRequestFlow";
import { useServiceRequest } from "@modules/ServiceRequest/hooks/useServiceRequest";
import { useTrackSpProfileOpened } from "@modules/ServiceRequest/hooks/useTrackSpProfileOpened";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "@modules/ServiceRequest/Components/messages";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import { FAQSection } from "@modules/CustomerSupport/Components/FAQSection/FAQSection";
import { Box } from "@UI/Components/Box/Box";
import { FrequentJobBanner } from "@modules/ServiceRequest/Components/ServiceRequestNextSteps/FrequentJobBanner";
import { TaskCreationSuccessModalRoot } from "@modules/ServiceRequest/ServiceRequestCreation/Containers/TaskCreationSuccessModalRoot/TaskCreationSuccessModalRoot";

export const ServiceRequest = withFlow(ServiceRequestFlow, () => {
  const { backRoute, serviceRequest } = useServiceRequestFlow();
  const { setState } = useFlow(ServiceRequestFlow);
  const { isMobile } = useMediaQuery();
  const { id } = useParams(["SERVICE_REQUEST"]);

  const navigate = useNavigate();

  useTrackSpProfileOpened();

const isNewTaskFlow = !!serviceRequest?.answers?.length;
const isCreatedBySP =
  serviceRequest?.authorId && serviceRequest?.authorId !== serviceRequest?.userId;


  const { data: fetchedServiceRequest } = useServiceRequest(
    id ?? "",
    !serviceRequest?.id && !!id
  );

  useEffect(() => {
    if (fetchedServiceRequest) {
      setState?.({ serviceRequest: fetchedServiceRequest });
    }
  }, [fetchedServiceRequest, setState]);

  // Read fresh on every fire without depending on `backRoute`/`navigate` in
  // the effect below, which only wants to arm once per mount.
  const handleBrowserBackRef = useRef(() =>
    navigate(backRoute.to, { reload: true })
  );
  handleBrowserBackRef.current = () => navigate(backRoute.to, { reload: true });

  useEffect(() => {
    // Same trick as the task-creation flow's `useBrowserBackGuard`: a
    // duplicate history entry means the native back button pops to an entry
    // with this same URL rather than leaving to a different document, so
    // `popstate` fires here instead of silently navigating away — which is
    // what let the browser Back button diverge from the page's own Back link
    // above. No need to re-push after firing: this always leaves the page.
    window.history.pushState(null, "", window.location.href);

    const onPopState = () => handleBrowserBackRef.current();

    window.addEventListener("popstate", onPopState);

    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  return (
    <>
      <TaskCreationSuccessModalRoot serviceRequestId={id} />
      <Wizard
        paddingTop={{ base: 5, md: 8 }}
        maxW={["container.md", "container.sm", "container.md", "container.xl"]}
        minHeight="auto"
      >
        <WizardNav>
          <BackLink
            text={<FormattedMessage {...messages["goToMyDemands"]} />}
            onClick={() => {
              navigate(backRoute.to, {
                reload: true,
              });
            }}
          />
        </WizardNav>
        <WizardContent>
          <Stack direction={{ base: "column", lg: "row" }} spacing="32px">
            <VStack w="full" alignItems="start" spacing="0">
              {/* START: Task detail card */}
              {isCreatedBySP ? (
                <ServiceRequestDetailCardSpInitiated />
              ) : isNewTaskFlow ? (
                <ServiceRequestDetailCardAiAssisted />
              ) : (
                <ServiceRequestDetailCard />
              )}
              {/* END: Task detail card */}
              {!isCreatedBySP &&
                serviceRequest?.recurrencyIntent === "frequent" &&
                serviceRequest?.authorFirstName && (
                  <Box display={{ base: "block", lg: "none" }} w="full" pt="16px">
                    <FrequentJobBanner spName={serviceRequest.authorFirstName} />
                  </Box>
                )}
              <CandidatesSection />
            </VStack>
            <ServiceRequestNextSteps />
          </Stack>
          {isMobile && (
            <Box marginTop="10">
              <FAQSection />
            </Box>
          )}
        </WizardContent>
      </Wizard>
      {!isMobile && (
        <Container
          marginY={[10, 10, 20]}
          maxW={["container.lg", "container.md", "container.lg"]}
        >
          <FAQSection />
        </Container>
      )}
    </>
  );
});
